/**
 * Session Monitor Service
 * Provides centralized session monitoring, token refresh, and idle detection
 */

import { keycloakService } from '@/services/auth/keycloakService';
import { authService } from '@/services/auth/authService';
import { auditLogger } from '@/services/auth/auditLogger';
import type { SessionConfig, SessionState, SessionTimerInfo, TabSyncMessage } from '@/types/session';

// Default configuration
const DEFAULT_CONFIG: SessionConfig = {
  tokenRefreshThreshold: 5 * 60 * 1000, // 5 minutes
  idleTimeout: 30 * 60 * 1000, // 30 minutes
  idleWarningTime: 2 * 60 * 1000, // 2 minutes
  sessionWarningTime: 2 * 60 * 1000, // 2 minutes
  sessionCheckInterval: 10 * 1000, // 10 seconds
  enableIdleDetection: true,
  enableTabSync: true,
  enableSilentRefresh: true,
  enableSessionWarning: true,
};

class SessionMonitorService {
  private config: SessionConfig = DEFAULT_CONFIG;
  private state: SessionState = {
    isAuthenticated: false,
    tokenExpiresAt: null,
    refreshTokenExpiresAt: null,
    lastActivity: Date.now(),
    sessionWarningShown: false,
  };

  // Event handlers
  private sessionExpiredHandlers: Array<() => void> = [];
  private sessionWarningHandlers: Array<(timeRemaining: number) => void> = [];
  private idleWarningHandlers: Array<(timeRemaining: number) => void> = [];
  private tokenRefreshedHandlers: Array<() => void> = [];
  private activityDetectedHandlers: Array<() => void> = [];

  // Timers
  private sessionCheckTimer: NodeJS.Timeout | null = null;
  private activityListenerAttached = false;

  // BroadcastChannel for tab synchronization
  private broadcastChannel: BroadcastChannel | null = null;
  private isLoggingOut = false; // Flag to prevent duplicate logout processing

  constructor() {
    this.initializeBroadcastChannel();
  }

  /**
   * Initialize BroadcastChannel for tab synchronization
   */
  private initializeBroadcastChannel(): void {
    if (typeof window === 'undefined' || !window.BroadcastChannel) {
      console.warn('BroadcastChannel not supported in this browser');
      return;
    }

    try {
      this.broadcastChannel = new BroadcastChannel('session-sync');
      
      this.broadcastChannel.onmessage = (event: MessageEvent<TabSyncMessage>) => {
        this.handleTabSyncMessage(event.data);
      };

      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 SessionMonitor: BroadcastChannel initialized for tab sync');
      }
    } catch (error) {
      console.error('Failed to initialize BroadcastChannel:', error);
    }
  }

  /**
   * Handle messages from other tabs
   */
  private handleTabSyncMessage(message: TabSyncMessage): void {
    if (process.env.NODE_ENV === 'development') {
      console.log('📨 SessionMonitor: Received tab sync message:', message.type);
    }

    switch (message.type) {
      case 'LOGOUT':
        // Another tab logged out - clear state and redirect
        auditLogger.logUserAction('SESSION_SYNC_LOGOUT', {
          source: 'other_tab',
          timestamp: new Date().toISOString(),
        });
        this.handleLogoutSync();
        break;

      case 'TOKEN_REFRESH':
        // Another tab refreshed the token - update local state
        this.updateSessionState();
        break;

      case 'ACTIVITY':
        // User activity detected in another tab - update last activity
        this.state.lastActivity = message.timestamp;
        break;

      case 'SESSION_EXTENDED':
        // User extended session in another tab - hide warning
        this.state.sessionWarningShown = false;
        break;
    }
  }

  /**
   * Broadcast message to other tabs
   */
  private broadcastToTabs(message: TabSyncMessage): void {
    if (!this.broadcastChannel) return;

    try {
      this.broadcastChannel.postMessage(message);
    } catch (error) {
      console.error('Failed to broadcast message:', error);
    }
  }

  /**
   * Broadcast logout to all tabs
   * This should be called when user clicks logout in any tab
   */
  broadcastLogout(): void {
    if (this.isLoggingOut) {
      // Already in logout process, don't broadcast again
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('📡 SessionMonitor: Broadcasting logout to all tabs...');
    }

    // Set flag to prevent duplicate processing
    this.isLoggingOut = true;

    // Broadcast logout message to all tabs
    this.broadcastToTabs({
      type: 'LOGOUT',
      timestamp: Date.now(),
      data: {
        source: 'user_action',
      },
    });

    // Also handle logout in this tab (the one that initiated logout)
    // Note: We don't call handleLogoutSync() here because the calling code
    // (authService.logout()) will handle the logout flow for this tab
  }

  /**
   * Handle logout sync from another tab
   */
  private handleLogoutSync(): void {
    // Prevent duplicate logout processing
    if (this.isLoggingOut) {
      if (process.env.NODE_ENV === 'development') {
        console.log('⚠️ SessionMonitor: Already logging out, ignoring duplicate message');
      }
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('📨 SessionMonitor: Received logout from another tab, clearing state...');
    }

    // Set flag to prevent duplicate processing
    this.isLoggingOut = true;

    // Stop monitoring
    this.stop();
    
    // Clear auth data
    authService.clearAuthData();
    
    // Redirect to login
    window.location.href = '/user/login';
  }

  /**
   * Configure session monitor
   */
  configure(config: Partial<SessionConfig>): void {
    this.config = { ...this.config, ...config };
    
    if (process.env.NODE_ENV === 'development') {
      console.log('⚙️ SessionMonitor configured:', this.config);
    }
  }

  /**
   * Start monitoring session
   */
  start(): void {
    if (this.sessionCheckTimer) {
      console.warn('SessionMonitor already started');
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('▶️ SessionMonitor: Starting session monitoring');
    }

    // Reset logout flag when starting a new session
    this.isLoggingOut = false;

    // Update initial state
    this.updateSessionState();

    // Start periodic checks
    this.sessionCheckTimer = setInterval(() => {
      this.performSessionCheck();
    }, this.config.sessionCheckInterval);

    // Attach activity listeners for idle detection
    if (this.config.enableIdleDetection) {
      this.attachActivityListeners();
    }

    // Log session monitoring started
    auditLogger.logUserAction('SESSION_MONITORING_STARTED', {
      config: this.config,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Stop monitoring session
   */
  stop(): void {
    if (this.sessionCheckTimer) {
      clearInterval(this.sessionCheckTimer);
      this.sessionCheckTimer = null;
    }

    this.detachActivityListeners();

    if (process.env.NODE_ENV === 'development') {
      console.log('⏹️ SessionMonitor: Stopped session monitoring');
    }
  }

  /**
   * Update session state from Keycloak
   */
  private updateSessionState(): void {
    const tokenInfo = keycloakService.getTokenInfo();
    
    if (!tokenInfo) {
      this.state.isAuthenticated = false;
      this.state.tokenExpiresAt = null;
      this.state.refreshTokenExpiresAt = null;
      return;
    }

    this.state.isAuthenticated = keycloakService.isAuthenticated();
    this.state.tokenExpiresAt = Date.now() + tokenInfo.expiresIn;
    
    // Refresh token typically expires after a longer period
    // For now, we'll estimate based on access token
    this.state.refreshTokenExpiresAt = Date.now() + (tokenInfo.expiresIn * 10);
  }

  /**
   * Perform periodic session check
   */
  private async performSessionCheck(): Promise<void> {
    // Check if still authenticated
    if (!keycloakService.isAuthenticated()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('⚠️ SessionMonitor: Not authenticated');
      }
      this.emitSessionExpired();
      return;
    }

    // Update session state
    this.updateSessionState();

    const now = Date.now();
    const timerInfo = this.getSessionTimerInfo();

    // Check for idle timeout
    if (this.config.enableIdleDetection) {
      const timeSinceActivity = now - this.state.lastActivity;
      
      if (timeSinceActivity >= this.config.idleTimeout) {
        // User is idle for too long - logout
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ SessionMonitor: Idle timeout reached');
        }
        
        auditLogger.logUserAction('SESSION_IDLE_TIMEOUT', {
          idleDuration: timeSinceActivity,
          timestamp: new Date().toISOString(),
        });
        
        await authService.logout();
        return;
      }

      // Check for idle warning
      const timeUntilIdle = this.config.idleTimeout - timeSinceActivity;
      if (timeUntilIdle <= this.config.idleWarningTime && !this.state.sessionWarningShown) {
        this.emitIdleWarning(Math.floor(timeUntilIdle / 1000));
      }
    }

    // ✅ SESSION VALIDATION: Handled by Keycloak's native iframe check
    // - checkLoginIframe polls Keycloak server every 60 seconds
    // - onAuthLogout event fires when session terminates (logout, idle, admin termination)
    // - onTokenExpired event fires when token expires - Keycloak handles refresh automatically
    // - NO proactive token refresh - rely entirely on Keycloak's onTokenExpired event
    
    // ⚠️ IMPORTANT: We DO NOT refresh tokens proactively
    // - Token refresh is handled by Keycloak's onTokenExpired event only
    // - This prevents resetting Keycloak's idle timer with automatic refreshes
    // - If user is idle, token expires naturally and session terminates
    // - sessionMonitor only tracks state and shows warnings - doesn't trigger refreshes

    // Check if we should show session warning (SESSION IDLE TIMER about to expire)
    // IMPORTANT: Session warnings are based on session idle time, NOT token expiration
    // This ensures users are warned about inactivity-based session termination
    if (this.config.enableSessionWarning && 
        timerInfo.isAboutToExpire && 
        !this.state.sessionWarningShown) {
      this.state.sessionWarningShown = true;
      // Use session idle time remaining, not token expiration time
      this.emitSessionWarning(Math.floor(timerInfo.idleTimeRemaining / 1000));
    }
  }

  /**
   * Refresh access token (MANUAL ONLY - for extendSession)
   * NOTE: Automatic token refresh is handled by Keycloak's onTokenExpired event
   * This method is only called when user manually clicks "Continue" on warning modal
   */
  async refreshToken(): Promise<boolean> {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 SessionMonitor: Manually refreshing token (user clicked Continue)...');
      }

      const refreshed = await authService.refreshAccessToken();
      
      if (refreshed) {
        // Update state
        this.updateSessionState();
        
        // Reset warning flag
        this.state.sessionWarningShown = false;
        
        // Broadcast to other tabs
        this.broadcastToTabs({
          type: 'TOKEN_REFRESH',
          timestamp: Date.now(),
        });
        
        // Emit event
        this.emitTokenRefreshed();
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ SessionMonitor: Token refreshed successfully');
        }
        
        return true;
      }

      console.error('❌ SessionMonitor: Token refresh failed');
      return false;
    } catch (error) {
      console.error('❌ SessionMonitor: Token refresh error:', error);
      return false;
    }
  }

  /**
   * Extend session (user clicked "Continue" on warning)
   */
  async extendSession(): Promise<boolean> {
    const success = await this.refreshToken();
    
    if (success) {
      // Reset activity timestamp
      this.recordActivity();
      
      // Hide warning
      this.state.sessionWarningShown = false;
      
      // Broadcast to other tabs
      this.broadcastToTabs({
        type: 'SESSION_EXTENDED',
        timestamp: Date.now(),
      });
      
      auditLogger.logUserAction('SESSION_EXTENDED', {
        timestamp: new Date().toISOString(),
      });
    }
    
    return success;
  }

  /**
   * Get current session timer information
   * IMPORTANT: Session warnings and termination are based on SESSION IDLE TIMER, not token expiration
   */
  getSessionTimerInfo(): SessionTimerInfo {
    const now = Date.now();
    const tokenExpiresIn = this.state.tokenExpiresAt 
      ? Math.max(0, this.state.tokenExpiresAt - now)
      : 0;
    
    const idleTimeRemaining = this.config.enableIdleDetection
      ? Math.max(0, this.config.idleTimeout - (now - this.state.lastActivity))
      : Infinity;
    
    // Session warnings based on SESSION IDLE TIMER, not token expiration
    // This ensures session terminates based on user inactivity, not token expiry
    const isAboutToExpire = this.config.enableIdleDetection && isFinite(idleTimeRemaining)
      ? idleTimeRemaining <= this.config.sessionWarningTime
      : false;
    const isIdle = idleTimeRemaining <= this.config.idleWarningTime;

    return {
      tokenExpiresIn,
      idleTimeRemaining,
      isAboutToExpire,
      isIdle,
    };
  }

  /**
   * Record user activity
   */
  recordActivity(): void {
    this.state.lastActivity = Date.now();
    
    // Broadcast to other tabs
    this.broadcastToTabs({
      type: 'ACTIVITY',
      timestamp: this.state.lastActivity,
    });
    
    // Emit activity detected event
    this.emitActivityDetected();
  }

  /**
   * Attach activity listeners for idle detection
   */
  private attachActivityListeners(): void {
    if (this.activityListenerAttached) return;

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle activity recording to avoid excessive updates
    let throttleTimeout: NodeJS.Timeout | null = null;
    const throttledRecordActivity = () => {
      if (throttleTimeout) return;
      
      this.recordActivity();
      
      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;
      }, 1000); // Throttle to once per second
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, throttledRecordActivity, { passive: true });
    });

    this.activityListenerAttached = true;

    if (process.env.NODE_ENV === 'development') {
      console.log('👂 SessionMonitor: Activity listeners attached');
    }
  }

  /**
   * Detach activity listeners
   */
  private detachActivityListeners(): void {
    if (!this.activityListenerAttached) return;

    // Remove listeners (simplified - in production, store listeners to remove correctly)
    this.activityListenerAttached = false;

    if (process.env.NODE_ENV === 'development') {
      console.log('🔇 SessionMonitor: Activity listeners detached');
    }
  }

  /**
   * Event handler registration
   */
  onSessionExpired(handler: () => void): void {
    this.sessionExpiredHandlers.push(handler);
  }

  onSessionWarning(handler: (timeRemaining: number) => void): void {
    this.sessionWarningHandlers.push(handler);
  }

  onIdleWarning(handler: (timeRemaining: number) => void): void {
    this.idleWarningHandlers.push(handler);
  }

  onTokenRefreshed(handler: () => void): void {
    this.tokenRefreshedHandlers.push(handler);
  }

  onActivityDetected(handler: () => void): void {
    this.activityDetectedHandlers.push(handler);
  }

  /**
   * Event emitters
   */
  private emitSessionExpired(): void {
    this.sessionExpiredHandlers.forEach(handler => {
      try {
        handler();
      } catch (error) {
        console.error('Error in session expired handler:', error);
      }
    });
  }

  private emitSessionWarning(timeRemaining: number): void {
    this.sessionWarningHandlers.forEach(handler => {
      try {
        handler(timeRemaining);
      } catch (error) {
        console.error('Error in session warning handler:', error);
      }
    });
  }

  private emitIdleWarning(timeRemaining: number): void {
    this.idleWarningHandlers.forEach(handler => {
      try {
        handler(timeRemaining);
      } catch (error) {
        console.error('Error in idle warning handler:', error);
      }
    });
  }

  private emitTokenRefreshed(): void {
    this.tokenRefreshedHandlers.forEach(handler => {
      try {
        handler();
      } catch (error) {
        console.error('Error in token refreshed handler:', error);
      }
    });
  }

  private emitActivityDetected(): void {
    this.activityDetectedHandlers.forEach(handler => {
      try {
        handler();
      } catch (error) {
        console.error('Error in activity detected handler:', error);
      }
    });
  }

  /**
   * Cleanup on logout
   */
  cleanup(): void {
    this.stop();
    
    // Broadcast logout to other tabs
    this.broadcastToTabs({
      type: 'LOGOUT',
      timestamp: Date.now(),
    });
    
    // Reset state
    this.state = {
      isAuthenticated: false,
      tokenExpiresAt: null,
      refreshTokenExpiresAt: null,
      lastActivity: Date.now(),
      sessionWarningShown: false,
    };
  }

  /**
   * Get current state (for debugging)
   */
  getState(): SessionState {
    return { ...this.state };
  }
}

export const sessionMonitor = new SessionMonitorService();
export default sessionMonitor;

